import { RegistrationFieldConfig } from 'interfaces/subscribe.types';

export const ADDRESS_FIELDS = [
  'addressTitle',
  'addressGender',
  'addressLastName',
  'addressFirstName',
  'addressCompanyName',
  'addressLine1',
  'addressLine2',
  'addressZipCode',
  'addressCity',
  'addressCountry',
] as const;

/**
 * Labels i18n par défaut pour chaque champ API,
 * utilisés si le BO ne fournit pas de label personnalisé.
 */
export const DEFAULT_LABEL_KEYS: Record<string, string> = {
  gender: 'auth.signup.civility.label',
  lastName: 'auth.signup.lastname',
  firstName: 'auth.signup.firstname',
  companyName: 'auth.signup.companyName',
  email: 'auth.global.email',
  clientId: 'auth.signup.matricule',
  phone: 'auth.signup.phone',
  password: 'auth.global.password',
  addressTitle: 'auth.signup.address.name',
  addressGender: 'auth.signup.address.civility',
  addressLastName: 'auth.signup.address.lastName',
  addressFirstName: 'auth.signup.address.firstName',
  addressCompanyName: 'auth.signup.address.companyName',
  addressLine1: 'auth.signup.address.street',
  addressLine2: 'auth.signup.address.complement',
  addressZipCode: 'auth.signup.address.postalCode',
  addressCity: 'auth.signup.address.city',
  addressCountry: 'auth.signup.address.country',
};

/**
 * Tooltips i18n par défaut pour certains champs.
 */
export const DEFAULT_TOOLTIP_KEYS: Record<string, string> = {
  email: 'auth.signup.tooltipTextEmail',
  clientId: 'auth.signup.tooltipTextMatricule',
};

/**
 * Détermine si un champ doit être affiché.
 * - display_rule_configurable = false → toujours visible
 * - display_rule_configurable = true :
 *     - display_rule = null ou "hidden" → masqué
 *     - display_rule = "editable_and_mandatory", "visible_and_editable",
 *       "visible_but_disabled" → visible
 */
export function isFieldVisible(cfg: RegistrationFieldConfig): boolean {
  if (!cfg.display_rule_configurable) return true;
  return cfg.display_rule != null && cfg.display_rule !== 'hidden';
}

/**
 * Détermine si un champ est désactivé (visible mais non éditable).
 * Un champ est désactivé si display_rule === 'visible_but_disabled'.
 */
export function isFieldDisabled(cfg: RegistrationFieldConfig): boolean {
  return cfg.display_rule === 'visible_but_disabled';
}

/**
 * Détermine si un champ est requis.
 * Un champ est obligatoire si (mandatory === true OU display_rule === 'editable_and_mandatory')
 * ET qu'il n'est pas désactivé (visible_but_disabled).
 * Note : "visible_and_editable" rend le champ éditable mais NON obligatoire.
 */
export function isFieldRequired(cfg: RegistrationFieldConfig): boolean {
  if (isFieldDisabled(cfg)) return false;
  return cfg.mandatory || cfg.display_rule === 'editable_and_mandatory';
}

/**
 * Variante pour les champs d'adresse dans les contextes pre-registration et autonomous-registration.
 * Un champ d'adresse n'est obligatoire (validation frontend) QUE si display_rule === 'editable_and_mandatory'.
 */
export function isAddressFieldRequired(cfg: RegistrationFieldConfig): boolean {
  if (isFieldDisabled(cfg)) return false;
  return cfg.display_rule === 'editable_and_mandatory';
}

/**
 * Récupère le label effectif : label personnalisé du BO, sinon traduction par défaut.
 */
export function getFieldLabel(
  cfg: RegistrationFieldConfig,
  t: (key: string) => string,
): string {
  if (cfg.label && cfg.label.trim().length > 0) return cfg.label;
  const key = DEFAULT_LABEL_KEYS[cfg.name];
  return key ? t(key) : cfg.name;
}

/**
 * Récupère le tooltip effectif : tooltip personnalisé du BO, sinon traduction par défaut.
 */
export function getFieldTooltip(
  cfg: RegistrationFieldConfig,
  t: (key: string) => string,
): string | undefined {
  if (cfg.tooltip && cfg.tooltip.trim().length > 0) return cfg.tooltip;
  const key = DEFAULT_TOOLTIP_KEYS[cfg.name];
  return key ? t(key) : undefined;
}
